import { useRegisterMutation, useLoginMutation, RegisterForm, LoginForm } from '../../features/api/authentication-api';
import {isReservedUsername} from "../../shared/usernames";
import {passwordProblem} from "../../shared/password-policy";
import { describeApiError } from '../../features/api/errors';
import * as Authentication from '../../features/authentication/authentication-slice';
import { store } from '../../store';
import { FieldValues, useForm } from 'react-hook-form';
import "./Register.scss";
import { useEffect, useState } from 'react';
import Button from "react-bootstrap/Button";
import Spinner from "../Spinner";

class RegisterProps {
    loginClicked?: (notice?: string) => void;
    registerSuccess?: () => void;
}

function Register(props: RegisterProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        watch,
        clearErrors
    } = useForm<RegisterForm>();

    const [sendRegister] = useRegisterMutation();
    const [login] = useLoginMutation();
    const [stage, setStage] = useState<"idle" | "registering" | "signing-in" | "done">("idle");
    const busy = stage === "registering" || stage === "signing-in" || stage === "done";

    async function onSubmit(data: any) {
        if (busy) return;
        setStage("registering");
        try {
            await sendRegister({ username: data.username.trim(), email: data.email.trim(), password: data.password }).unwrap();
        } catch (error) {
            setStage("idle");
            setError("root.server", {type: "server", message: describeApiError(error, "register")});
            return;
        }
        // Account created: sign straight in.
        setStage("signing-in");
        try {
            const result = await login({email: data.email.trim(), password: data.password} as LoginForm).unwrap();
            setStage("done");
            store.dispatch(Authentication.login({newState: {...result, id: result.id == null ? null : String(result.id)}}));
            props.registerSuccess?.();
        } catch (error) {
            setStage("idle");
            props.loginClicked?.("Account created! Please sign in.");
        }
    }

    useEffect(() => {
        const subscription = watch(() => clearErrors("root.server"));
        return () => subscription.unsubscribe();
    }, [watch, clearErrors]);

    return <div id='register-box' className="card bg-light auth-box">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <h2>Create account</h2>
            <div className="mb-2">
                <label htmlFor='reg-username' className="form-label">Player name</label>
                <input className="form-control" id="reg-username" autoComplete="nickname" disabled={busy} maxLength={64}
                       {...register("username", { required: "Player name is required",
                           validate: (v: string) => v.trim().length === 0 ? "Player name is required"
                               : isReservedUsername(v) ? "Names like \"Guest-1\", \"Guest 1\" or \"guest1\" are reserved for guests" : true })} type='text' />
                <span className="error-msg">{errors.username?.message?.toString()}</span>
            </div>
            <div className="mb-2">
                <label htmlFor='reg-email' className="form-label">Email</label>
                <input className="form-control" id="reg-email" autoComplete="email" disabled={busy}
                       {...register("email", { required: "Email is required",
                           pattern: {value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email"} })} type='email' />
                <span className="error-msg">{errors.email?.message?.toString()}</span>
            </div>
            <div className="mb-2">
                <label htmlFor='reg-password' className="form-label">Password</label>
                <input className="form-control" id="reg-password" autoComplete="new-password" disabled={busy}
                       {...register('password', {
                    // length in characters (code points), same rule as the server
                    required: 'Password is required', validate: (v: string) => passwordProblem(v) ?? true,
                })} type='password' />
                <span className="error-msg">{errors.password?.message?.toString()}</span>
            </div>
            <div className="mb-2">
                <label htmlFor='reg-confirm' className="form-label">Confirm password</label>
                <input className="form-control" id="reg-confirm" autoComplete="new-password" disabled={busy}
                       {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: {
                        mustMatch: (value: unknown, formValues: FieldValues) => {
                            return value === formValues.password || "Passwords must match"
                        }
                    }
                })} type='password' />
                <span className="error-msg">{errors.confirmPassword?.message?.toString()}</span>
            </div>

            {errors.root?.server ? <div className="alert alert-danger py-2" role="alert">{errors.root.server.message}</div> : null}
            {stage === "signing-in" || stage === "done" ? <div className="alert alert-success py-2" role="status">Account created! Signing you in…</div> : null}

            <div className="bottom-buttons">
                <Button type="submit" id='btn-register' disabled={busy}>
                    {stage === "registering" ? <><Spinner/>Registering…</> : stage === "signing-in" || stage === "done" ? <><Spinner/>Signing in…</> : "Register"}
                </Button>
                <hr />
                <span id="sign-in-link">Already have an account?</span>
                <Button id="sign-in-button" variant="secondary" disabled={busy} onClick={ () => {props.loginClicked?.()} }>
                    Sign in
                </Button>
            </div>
        </form>
    </div>;
}

export default Register;
